import React, { useEffect, useMemo, useState } from 'react';
import { newsAPI } from '@/services/newsService';
import { getImageUrl } from '@/services/api';
import { Search, Plus, PencilLine, Trash2, Star, Eye, EyeOff, CalendarDays, Newspaper } from 'lucide-react';

const initialForm = {
  title: '',
  excerpt: '',
  content: '',
  category: 'ข่าวสาร',
  author: '',
  cover_image_url: '',
  publish_date: '',
  is_featured: false,
  is_active: true,
  display_order: 0,
};

const resolveCoverUrl = (value) => {
  const v = (value || '').trim();
  if (!v) return '';
  return getImageUrl(v);
};

const NewsAdmin = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState('');

  const sorted = useMemo(() => [...items].sort((a, b) => b.id - a.id), [items]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((item) =>
      [item.title, item.excerpt, item.author, item.category].filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [sorted, query]);
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((i) => Boolean(i.is_active)).length,
    featured: items.filter((i) => Boolean(i.is_featured)).length,
  }), [items]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await newsAPI.listAdmin();
      setItems(res.data || []);
    } catch (error) {
      console.error(error);
      alert(error.message || 'โหลดข่าวไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api'}/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body,
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Upload endpoint returned non-JSON response (${res.status})`);
      }
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || data?.message || 'อัปโหลดไม่สำเร็จ');
      const uploadedUrl = data.fileUrl || data.url || data.s3Location || '';
      if (!uploadedUrl) throw new Error('ไม่พบ URL รูปจากเซิร์ฟเวอร์');
      setForm((prev) => ({ ...prev, cover_image_url: uploadedUrl }));
    } catch (error) {
      console.error(error);
      alert(error.message || 'อัปโหลดไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (!form.title.trim()) return alert('กรุณากรอกหัวข้อข่าว');
      const payload = { ...form, display_order: Number(form.display_order || 0) };
      if (editingId) {
        await newsAPI.update(editingId, payload);
      } else {
        await newsAPI.create(payload);
      }
      setForm(initialForm);
      setEditingId(null);
      setShowForm(false);
      await load();
      alert('บันทึกเรียบร้อย');
    } catch (error) {
      console.error(error);
      alert(error.message || 'บันทึกไม่สำเร็จ');
    }
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setShowForm(true);
    setForm({
      title: item.title || '',
      excerpt: item.excerpt || '',
      content: item.content || '',
      category: item.category || 'ข่าวสาร',
      author: item.author || '',
      cover_image_url: item.cover_image_url || '',
      publish_date: item.publish_date ? String(item.publish_date).slice(0, 10) : '',
      is_featured: Boolean(item.is_featured),
      is_active: Boolean(item.is_active),
      display_order: item.display_order || 0,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (id) => {
    if (!window.confirm('ยืนยันการลบบทความนี้?')) return;
    try {
      await newsAPI.remove(id);
      await load();
    } catch (error) {
      console.error(error);
      alert(error.message || 'ลบไม่สำเร็จ');
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[#e8ddff] bg-gradient-to-r from-[#f9f6ff] to-[#f3fff7] p-6 md:p-7">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <p className="inline-flex items-center gap-2 text-sm font-medium text-[#6a4bb3] mb-2">
              <Newspaper className="w-4 h-4" />
              News Content Management
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">จัดการข่าวสาร</h1>
            <p className="text-gray-600">ดูรายการข่าวทั้งหมด ค้นหา แก้ไข และเผยแพร่ได้จากหน้าจอเดียว</p>
          </div>
          <button
            onClick={() => {
              setEditingId(null);
              setForm(initialForm);
              setShowForm(true);
            }}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#5b35b0] text-white hover:bg-[#4a2895] whitespace-nowrap shadow-sm"
          >
            <Plus className="w-4 h-4" />
            เพิ่มข่าวใหม่
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">ข่าวทั้งหมด</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">กำลังเผยแพร่</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.active}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">ข่าวเด่น</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{stats.featured}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold text-gray-900">รายการข่าวทั้งหมด</h2>
          <div className="relative w-full md:w-[320px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ค้นหาด้วยชื่อข่าว / หมวดหมู่ / ผู้เขียน"
              className="w-full border rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500">กำลังโหลด...</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => (
              <div key={item.id} className="flex flex-col md:flex-row md:items-center gap-4 border rounded-xl p-3.5 hover:shadow-sm transition">
                {resolveCoverUrl(item.cover_image_url) ? (
                  <img
                    src={resolveCoverUrl(item.cover_image_url)}
                    alt={item.title}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                    className="w-full md:w-32 h-24 object-cover rounded-lg bg-gray-100"
                  />
                ) : (
                  <div className="w-full md:w-32 h-24 rounded-lg bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-500">
                    ไม่มีรูปปก
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="text-xs rounded-full bg-purple-50 text-purple-700 border border-purple-100 px-2.5 py-1">
                      {item.category || 'ข่าวสาร'}
                    </span>
                    {Boolean(item.is_featured) && (
                      <span className="inline-flex items-center gap-1 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2.5 py-1">
                        <Star className="w-3.5 h-3.5" />
                        ข่าวเด่น
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 border ${Boolean(item.is_active) ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {Boolean(item.is_active) ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      {Boolean(item.is_active) ? 'เผยแพร่' : 'ซ่อน'}
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900 line-clamp-1">{item.title}</p>
                  <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{item.excerpt || '-'}</p>
                  <div className="mt-2 text-xs text-gray-500 flex flex-wrap items-center gap-3">
                    <span>ผู้เขียน: {item.author || 'ทีมงาน'}</span>
                    <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{item.publish_date ? String(item.publish_date).slice(0, 10) : '-'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onEdit(item)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
                  >
                    <PencilLine className="w-4 h-4" />
                    แก้ไข
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                    ลบ
                  </button>
                </div>
              </div>
            ))}
            {!filtered.length && <p className="text-gray-500">ไม่พบรายการข่าวที่ตรงกับคำค้นหา</p>}
          </div>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border p-6">
          <h2 className="text-xl font-semibold mb-1 text-gray-900">{editingId ? 'แก้ไขข่าว' : 'เพิ่มข่าวใหม่'}</h2>
          <p className="text-gray-500 mb-5">กรอกข้อมูลบทความข่าวสำหรับหน้า `/news`</p>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input name="title" value={form.title} onChange={onChange} placeholder="หัวข้อข่าว *" className="border rounded-xl px-3 py-2.5 md:col-span-2" />
            <input name="author" value={form.author} onChange={onChange} placeholder="ผู้เขียน" className="border rounded-xl px-3 py-2.5" />
            <input name="category" value={form.category} onChange={onChange} placeholder="หมวดหมู่" className="border rounded-xl px-3 py-2.5" />
            <input type="date" name="publish_date" value={form.publish_date} onChange={onChange} className="border rounded-xl px-3 py-2.5" />
            <input type="number" name="display_order" value={form.display_order} onChange={onChange} placeholder="ลำดับการแสดงผล" className="border rounded-xl px-3 py-2.5" />
            <textarea name="excerpt" value={form.excerpt} onChange={onChange} placeholder="สรุปข่าว (excerpt)" rows={3} className="border rounded-xl px-3 py-2.5 md:col-span-2" />
            <textarea name="content" value={form.content} onChange={onChange} placeholder="เนื้อหาข่าว (รองรับ HTML)" rows={8} className="border rounded-xl px-3 py-2.5 md:col-span-2" />

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
              <input name="cover_image_url" value={form.cover_image_url} onChange={onChange} placeholder="ลิงก์รูปปก หรือใช้ปุ่มอัปโหลด" className="border rounded-xl px-3 py-2.5" />
              <input type="file" accept="image/*" onChange={(e) => handleUpload(e.target.files?.[0])} className="border rounded-xl px-3 py-2.5" />
            </div>
            {uploading && <p className="md:col-span-2 text-sm text-purple-700">กำลังอัปโหลดรูป...</p>}
            {form.cover_image_url && (
              <div className="md:col-span-2">
                <img src={resolveCoverUrl(form.cover_image_url)} alt="preview" className="w-56 h-32 object-cover rounded-xl border" />
              </div>
            )}

            <label className="inline-flex items-center gap-2">
              <input type="checkbox" name="is_featured" checked={form.is_featured} onChange={onChange} />
              ข่าวเด่น
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
              เปิดแสดงบนเว็บ
            </label>

            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="px-4 py-2.5 rounded-xl bg-[#5b35b0] text-white hover:bg-[#4a2895]">
                {editingId ? 'อัปเดตข่าว' : 'สร้างข่าว'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(initialForm);
                }}
                className="px-4 py-2.5 rounded-xl border hover:bg-gray-50"
              >
                ปิดฟอร์ม
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default NewsAdmin;
